import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';

interface College {
  id?: number;
  name: string;
  description: string;
  total_quota: number;
  male_quota: number;
  female_quota: number;
  is_fallback: boolean;
}

const empty: College = {
  name: '', description: '', total_quota: 0,
  male_quota: 0, female_quota: 0, is_fallback: false
};

export default function Colleges() {
  const navigate = useNavigate();
  const [colleges, setColleges] = useState<College[]>([]);
  const [editing, setEditing] = useState<College | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchColleges = () => {
    api.get('/api/colleges')
      .then(res => { setColleges(res.data); setLoading(false); })
      .catch(() => navigate('/admin/login'));
  };

  useEffect(() => { fetchColleges(); }, []);

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await api.put(`/api/admin/colleges/${editing.id}`, editing);
      } else {
        await api.post('/api/admin/colleges', editing);
      }
      setEditing(null);
      fetchColleges();
    } catch (e) {
      alert('保存失败，请检查是否已登录');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确认删除该书院？')) return;
    await api.delete(`/api/admin/colleges/${id}`);
    fetchColleges();
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">书院管理</h1>
          <div className="flex gap-3">
            <button onClick={() => navigate('/admin/dashboard')}
              className="text-sm text-gray-500 hover:text-gray-700">← 返回</button>
            <button onClick={() => setEditing({ ...empty })}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700">
              + 新增书院
            </button>
          </div>
        </div>

        {loading ? <p className="text-gray-400">加载中...</p> : (
          <div className="space-y-4">
            {colleges.map(c => (
              <div key={c.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-800">{c.name}</h3>
                      {c.is_fallback && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">兜底书院</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{c.description}</p>
                    <div className="flex gap-4 mt-2 text-sm text-gray-600">
                      <span>总配额：{c.total_quota}</span>
                      <span>男生：{c.male_quota}</span>
                      <span>女生：{c.female_quota}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ ...c })}
                      className="text-sm text-indigo-600 hover:text-indigo-800">编辑</button>
                    <button onClick={() => handleDelete(c.id!)}
                      className="text-sm text-red-500 hover:text-red-700">删除</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <h2 className="text-lg font-bold mb-4">{editing.id ? '编辑书院' : '新增书院'}</h2>
              <div className="space-y-3">
                {[
                  { label: '书院名称', key: 'name', type: 'text' },
                  { label: '简介', key: 'description', type: 'text' },
                  { label: '总配额', key: 'total_quota', type: 'number' },
                  { label: '男生配额', key: 'male_quota', type: 'number' },
                  { label: '女生配额', key: 'female_quota', type: 'number' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                    <input
                      type={field.type}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      value={(editing as any)[field.key]}
                      onChange={e => setEditing({
                        ...editing,
                        [field.key]: field.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value
                      })}
                    />
                  </div>
                ))}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editing.is_fallback}
                    onChange={e => setEditing({ ...editing, is_fallback: e.target.checked })} />
                  <span className="text-sm text-gray-700">设为兜底书院</span>
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setEditing(null)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                  取消
                </button>
                <button onClick={handleSave}
                  className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700">
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}